// Client for the PUBLIC, unauthenticated share-link routes
// (internal/api/handlers/share_public.go) — deliberately NOT built on
// apiFetch: there's no bearer token, no 401-triggers-refresh dance, and a
// wrong/missing share password is a normal 401 the caller handles inline
// (show a password prompt), not an auth failure to bubble as an error.
import { API_BASE_URL } from '$lib/config';
import { downloadFile } from './client';
import type { ClientComment, SharedQuotation } from '$lib/types';

export type ShareFetchResult =
  | { status: 'ok'; quote: SharedQuotation }
  | { status: 'password_required' }
  | { status: 'not_found' };

async function parseDetail(response: Response): Promise<string | null> {
  const data: unknown = await response.json().catch(() => ({}));
  return typeof data === 'object' && data !== null && 'detail' in data
    ? String((data as { detail?: unknown }).detail)
    : null;
}

export const shareApi = {
  // password is whatever the visitor has typed so far — sent on every call
  // (see resolveSharedQuotation's doc comment on the Go side); empty string
  // is fine when the share isn't password-protected.
  async get(token: string, password: string): Promise<ShareFetchResult> {
    const response = await fetch(`${API_BASE_URL}/share/${encodeURIComponent(token)}`, {
      headers: password ? { 'X-Share-Password': password } : {}
    });
    if (response.status === 401) return { status: 'password_required' };
    if (response.status === 404) return { status: 'not_found' };
    if (!response.ok) throw new Error((await parseDetail(response)) || `HTTP ${response.status}`);
    return { status: 'ok', quote: (await response.json()) as SharedQuotation };
  },

  async postComment(
    token: string,
    password: string,
    body: { author_name: string; author_email: string; body: string }
  ): Promise<ClientComment> {
    const response = await fetch(`${API_BASE_URL}/share/${encodeURIComponent(token)}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(password ? { 'X-Share-Password': password } : {})
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error((await parseDetail(response)) || `HTTP ${response.status}`);
    return (await response.json()) as ClientComment;
  },

  downloadPdf: (token: string, password: string, formattedNumber: string) =>
    downloadFile(
      `/share/${encodeURIComponent(token)}/pdf`,
      password ? { 'X-Share-Password': password } : {},
      `${formattedNumber}.pdf`
    )
};
